import { useEffect, useRef, useState } from 'react'

import { Tooltip } from '@verbit-ai/verbit-ui-library'

import { ScaleIcon } from 'src/components/icons'
import { InlineInconsistency } from 'src/models/InlineInconsistency'

import * as S from './styles'

type InconsistencyIndicatorsProps = {
    /** The inconsistencies in one paragraph, in paragraph order. */
    inconsistencies: readonly InlineInconsistency[]
    /** The open inconsistency, if it is one of these. */
    selectedUuid?: string | null
    onSelect: (uuid: string) => void
}

/**
 * One scale icon in the paragraph's margin. Several inconsistencies show a count badge, and the icon turns into
 * a column of numbered circles while the paragraph is hovered, one of them is selected, or the icon is opened.
 */
export const InconsistencyIndicators = (props: InconsistencyIndicatorsProps) => {
    const { inconsistencies, selectedUuid, onSelect } = props
    const total = inconsistencies.length
    const [isOpen, setIsOpen] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)
    const iconRef = useRef<HTMLButtonElement>(null)
    const firstNumberRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (isOpen) {
            firstNumberRef.current?.focus()
        }
    }, [isOpen])

    // A list update can remove the focused number without a blur, leaving focus on the page: close instead of
    // staying open with nothing focused (where Esc would no longer reach it).
    useEffect(() => {
        const root = rootRef.current
        if (isOpen && (!root || !root.contains(document.activeElement))) {
            setIsOpen(false)
        }
    }, [inconsistencies, isOpen])

    if (total === 0) return null

    if (total === 1) {
        const [{ uuid, title }] = inconsistencies

        return (
            <S.Root data-testid="inconsistency-indicators">
                <Tooltip label={title} placement="right">
                    <S.IconButton
                        type="button"
                        aria-label={`Inconsistency: ${title}`}
                        aria-pressed={uuid === selectedUuid}
                        onClick={() => onSelect(uuid)}>
                        <ScaleIcon aria-hidden />
                    </S.IconButton>
                </Tooltip>
            </S.Root>
        )
    }

    const isSelectedHere = inconsistencies.some(({ uuid }) => uuid === selectedUuid)

    return (
        <S.Root
            ref={rootRef}
            role="group"
            aria-label="Inconsistencies"
            data-testid="inconsistency-indicators"
            data-expanded={isOpen || isSelectedHere}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setIsOpen(false)
                }
            }}
            onKeyDown={(event) => {
                if (event.key === 'Escape' && isOpen) {
                    setIsOpen(false)
                    iconRef.current?.focus()
                }
            }}>
            <S.IconButton
                ref={iconRef}
                type="button"
                data-multi
                aria-label={`${total} inconsistencies`}
                aria-expanded={isOpen || isSelectedHere}
                onClick={() => setIsOpen(true)}>
                <ScaleIcon aria-hidden />
                <S.Badge aria-hidden>{total}</S.Badge>
            </S.IconButton>
            <S.Numbers>
                {inconsistencies.map(({ uuid, title }, index) => (
                    <Tooltip key={uuid} label={title} placement="right">
                        <S.NumberButton
                            ref={index === 0 ? firstNumberRef : undefined}
                            type="button"
                            data-number
                            aria-label={`Inconsistency ${index + 1} of ${total}: ${title}`}
                            aria-pressed={uuid === selectedUuid}
                            onClick={() => onSelect(uuid)}>
                            {index + 1}
                        </S.NumberButton>
                    </Tooltip>
                ))}
            </S.Numbers>
        </S.Root>
    )
}
